import React from "react";
import { Plus, Trash2, FlipHorizontal2 } from "lucide-react";
import { GATE_DEFAULTS, vertexAngle } from "../../lib/planner";
import { NumInput } from "../NumInput";

const inputCls = "w-full px-2 py-1.5 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black";
const selCls = "w-full px-2 py-1.5 bg-white border border-zinc-300 rounded-sm text-sm";

export function SegmentTable({ layout, plan, selectedSeg, onSelectSeg, onLength, onAngle, onFlip, onRemoveSegment, onAddGate, onGate, onRemoveGate }) {
  if (!layout.segments.length) {
    return <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200" data-testid="planner-no-segments">
      Noch keine Segmente. Zeichnen Sie den Zaunverlauf auf dem Raster (Modus „Zeichnen“).
    </div>;
  }
  const gd = plan.posts.gate.dim;
  const presetOffset = (seg, gate, preset) => {
    const L = seg.length, W = Number(gate.width) || 0;
    if (preset === "start") return 0;
    if (preset === "end") return Math.max(0, L - W - gd);
    return Math.max(0, Math.round((L - W) / 2));
  };
  return (
    <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid="planner-segment-table">
      <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
        <div className="col-span-1">Seg.</div><div className="col-span-2">Länge (mm)</div><div className="col-span-2">Winkel zu vorh. (°)</div>
        <div className="col-span-3">Felder</div><div className="col-span-1">Pfosten</div><div className="col-span-3 text-right">Tore</div>
      </div>
      {layout.segments.map((s) => {
        const gates = plan.gates.filter((g) => g.seg === s.idx);
        const groups = Object.entries(s.fields.reduce((m, f) => ({ ...m, [f.width]: (m[f.width] || 0) + 1 }), {}));
        const va = vertexAngle(plan, s.idx);
        return (
          <div key={s.idx} className={`px-3 py-2 ${selectedSeg === s.idx ? "bg-amber-50/60" : ""}`} onClick={() => onSelectSeg(s.idx)} data-testid={`planner-seg-row-${s.idx}`}>
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 md:col-span-1 font-bold text-zinc-900">{s.label}</div>
              <div className="col-span-5 md:col-span-2">
                <NumInput step={100} min={200} value={s.length} disabled={s.isClosing} emptyValue={200} testId={`planner-seg-length-${s.idx}`}
                       onChange={(v) => onLength(s.idx, v)} className={`${inputCls} ${s.isClosing ? "opacity-60" : ""}`} title={s.isClosing ? "Schlusssegment – ergibt sich aus den anderen Segmenten" : ""} />
              </div>
              <div className="col-span-5 md:col-span-2">
                {va ? (
                  <div className="flex gap-1">
                    <NumInput step={5} min={1} max={179} value={va.angle} emptyValue={90} testId={`planner-seg-angle-${s.idx}`}
                           onChange={(v) => onAngle(s.idx, v)} className={inputCls} title="Winkel zwischen vorherigem Segment und diesem Segment" />
                    <button type="button" onClick={(e) => { e.stopPropagation(); onFlip(s.idx); }} data-testid={`planner-seg-flip-${s.idx}`}
                            className="shrink-0 px-1.5 border border-zinc-300 text-zinc-600 hover:text-zinc-900 hover:border-zinc-900 rounded-sm" title="Seite wechseln (links/rechts)">
                      <FlipHorizontal2 size={13}/>
                    </button>
                  </div>
                ) : <span className="text-xs text-zinc-400">—</span>}
              </div>
              <div className="col-span-8 md:col-span-3 text-sm text-zinc-800" data-testid={`planner-seg-fields-${s.idx}`}>
                {groups.length ? groups.map(([w, c]) => `${c} × ${w} mm`).join(", ") : <span className="text-zinc-400">—</span>}
              </div>
              <div className="col-span-4 md:col-span-1 text-sm text-zinc-700">{s.innerPosts.length} Stk.</div>
              <div className="col-span-12 md:col-span-3 flex items-center justify-end gap-2">
                <button onClick={(e) => { e.stopPropagation(); onAddGate(s.idx, presetOffset(s, { width: GATE_DEFAULTS.gehtuere.width }, "center")); }}
                        data-testid={`planner-add-gate-${s.idx}`}
                        className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 border border-zinc-900 text-zinc-900 rounded-sm hover:bg-zinc-900 hover:text-white">
                  <Plus size={12}/> Tor
                </button>
                <button onClick={(e) => { e.stopPropagation(); onRemoveSegment(s.idx); }} data-testid={`planner-seg-remove-${s.idx}`}
                        className="text-zinc-400 hover:text-red-600 p-1" title="Segment löschen">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
            {gates.map((g) => (
              <div key={g.id} className="mt-2 ml-0 md:ml-8 grid grid-cols-12 gap-2 items-end bg-blue-50/50 border border-blue-100 px-2 py-2" data-testid={`planner-gate-${g.id}`}>
                <div className="col-span-12 md:col-span-4">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-0.5">Tor-Typ</label>
                  <select value={g.type} onChange={(e) => onGate(g.id, { type: e.target.value, width: GATE_DEFAULTS[e.target.value].width })} className={selCls} data-testid={`planner-gate-type-${g.id}`}>
                    {Object.entries(GATE_DEFAULTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="col-span-5 md:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-0.5">Lichte (mm)</label>
                  <NumInput step={50} value={g.width} emptyValue={1000} onChange={(v) => onGate(g.id, { width: v })} className={inputCls} testId={`planner-gate-width-${g.id}`} />
                </div>
                <div className="col-span-7 md:col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-0.5">Position</label>
                  <select value="" onChange={(e) => e.target.value && onGate(g.id, { offset: presetOffset(s, g, e.target.value) })} className={selCls} data-testid={`planner-gate-preset-${g.id}`}>
                    <option value="">Ab Segmentanfang…</option>
                    <option value="start">Am Anfang</option>
                    <option value="center">In der Mitte</option>
                    <option value="end">Am Ende</option>
                  </select>
                </div>
                <div className="col-span-8 md:col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-0.5">Abstand (mm)</label>
                  <NumInput step={100} min={0} value={g.offset} emptyValue={0} onChange={(v) => onGate(g.id, { offset: v })} className={inputCls} testId={`planner-gate-offset-${g.id}`} />
                </div>
                <div className="col-span-4 md:col-span-1 text-right pb-1.5">
                  <button onClick={() => onRemoveGate(g.id)} className="text-zinc-400 hover:text-red-600" data-testid={`planner-gate-remove-${g.id}`}><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
